import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import EmployeeCombobox from './EmployeeCombobox';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Info,
  Users,
  FileText,
  Zap
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';
import { isAdmin } from '../utils/roleUtils';
import { useAuth } from '../contexts/AuthContext';

const EnhancedAbsenceForm = ({ 
  employees = [], 
  onSubmit, 
  onCancel, 
  initialData = null,
  currentUser = null,
  userRoles = []  // Accept userRoles as a separate prop for better reliability
}) => {
  // Get roles from AuthContext as fallback
  const { roles: authRoles = [] } = useAuth();
  
  // Determine if user is HR/Admin (Requirement 7.1, 7.2)
  // Use centralized role utility to ensure super_admin is always included
  // Try to get roles from userRoles prop first, then currentUser, then AuthContext
  const roles = userRoles.length > 0 ? userRoles : (currentUser?.roles || authRoles || []);
  const isHROrAdmin = isAdmin(roles);
  
  console.log('🔐 EnhancedAbsenceForm - Role check:', { 
    userRoles, 
    currentUserRoles: currentUser?.roles, 
    authRoles, 
    finalRoles: roles, 
    isHROrAdmin 
  });
  
  // For regular employees, lock employee_id to their own ID
  // Note: AuthContext provides 'oid' field, but we also check 'id' for compatibility
  const getInitialEmployeeId = () => {
    if (initialData?.employee_id) return initialData.employee_id;
    const userId = currentUser?.oid || currentUser?.id;
    if (!isHROrAdmin && userId) return userId;
    return userId || '';
  };
  
  const [formData, setFormData] = useState({
    employee_id: getInitialEmployeeId(),
    leave_type: initialData?.leave_type || 'PTO',
    start_date: initialData?.start_date || '',
    end_date: initialData?.end_date || '',
    start_time: initialData?.start_time || '',
    end_time: initialData?.end_time || '',
    total_hours: initialData?.total_hours || 8,
    total_minutes: initialData?.total_minutes || 0,
    purpose: initialData?.purpose || '',
    notes: initialData?.notes || '',
    requires_documentation: initialData?.requires_documentation || false,
    documentation_url: initialData?.documentation_url || '',
    coverage_arranged: initialData?.coverage_arranged || false,
    coverage_employee_ids: initialData?.coverage_employee_ids || [],
    emergency_contact: initialData?.emergency_contact || '',
    return_date_confirmed: initialData?.return_date_confirmed || true,
    policy_override_requested: false,
    policy_override_reason: ''
  });

  const [validation, setValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load templates and quick actions
  useEffect(() => {
    loadTemplatesAndQuickActions();
  }, []);

  // Find employee by email when currentUser is available (only for non-admin users)
  // Admins should be able to select any employee, so we don't auto-populate for them
  useEffect(() => {
    // Only auto-populate for non-admin users and if employee_id is not already set
    if (isHROrAdmin || formData.employee_id) return;
    
    if (currentUser && employees.length > 0) {
      const userEmail = currentUser.email || currentUser.username;
      const userName = currentUser.name || currentUser.displayName;
      
      console.log('🔍 Looking for employee match (non-admin user):', { userEmail, userName, employeeCount: employees.length });
      
      if (userEmail) {
        // Try to find employee by email
        const matchingEmployee = employees.find(emp => 
          emp.email?.toLowerCase() === userEmail.toLowerCase()
        );
        
        if (matchingEmployee) {
          console.log('✅ Found employee by email:', matchingEmployee.id, matchingEmployee.first_name, matchingEmployee.last_name);
          setFormData(prev => {
            if (!prev.employee_id) {
              return { ...prev, employee_id: matchingEmployee.id };
            }
            return prev;
          });
          return;
        }
      }
      
      // If no email match found, try to find by name as fallback
      if (userName) {
        const nameParts = userName.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts.slice(1).join(' ');
          
          const matchingByName = employees.find(emp => 
            emp.first_name?.toLowerCase() === firstName.toLowerCase() &&
            emp.last_name?.toLowerCase() === lastName.toLowerCase()
          );
          
          if (matchingByName) {
            console.log('✅ Found employee by name:', matchingByName.id, matchingByName.first_name, matchingByName.last_name);
            setFormData(prev => {
              if (!prev.employee_id) {
                return { ...prev, employee_id: matchingByName.id };
              }
              return prev;
            });
            return;
          }
        }
      }
      
      console.warn('⚠️ Could not find matching employee for current user');
    }
  }, [currentUser, employees, isHROrAdmin]);

  // Auto-validate when key fields change
  useEffect(() => {
    if (formData.employee_id && formData.start_date && formData.end_date && formData.leave_type) {
      validateRequest();
    }
  }, [formData.employee_id, formData.start_date, formData.end_date, formData.leave_type, formData.total_hours]);

  // Auto-calculate total time
  useEffect(() => {
    calculateTotalTime();
  }, [formData.start_date, formData.end_date, formData.start_time, formData.end_time]);

  const loadTemplatesAndQuickActions = async () => {
    try {
      const [templatesData, quickActionsData] = await Promise.all([
        apiService.makeRequest('/api/absences/templates'),
        apiService.makeRequest('/api/absences/quick-actions')
      ]);
      
      setTemplates(templatesData);
      setQuickActions(quickActionsData.quick_actions || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const calculateTotalTime = () => {
    if (!formData.start_date || !formData.end_date) return;
    
    const start = new Date(`${formData.start_date}T${formData.start_time || '08:00'}`);
    const end = new Date(`${formData.end_date}T${formData.end_time || '16:00'}`);
    
    if (formData.start_date === formData.end_date && formData.start_time && formData.end_time) {
      // Same day partial absence
      const diffMs = end.getTime() - start.getTime();
      const totalMinutes = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      setFormData(prev => ({
        ...prev,
        total_hours: hours,
        total_minutes: minutes
      }));
    } else {
      // Multi-day absence
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const totalHours = diffDays * 8;
      
      setFormData(prev => ({
        ...prev,
        total_hours: totalHours,
        total_minutes: 0
      }));
    }
  };

  const validateRequest = async () => {
    // Don't validate if required fields are missing
    if (!formData.employee_id || !formData.start_date || !formData.end_date || !formData.leave_type) {
      // Clear validation if we don't have enough data
      if (!formData.employee_id || !formData.start_date || !formData.end_date) {
        setValidation(null);
      }
      return;
    }
    
    setIsValidating(true);
    try {
      const validationResult = await apiService.makeRequest('/api/absences/validate', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      setValidation(validationResult);
      
      // Check for conflicts only if we have all required data
      if (formData.employee_id && formData.start_date && formData.end_date) {
        try {
          const conflictResult = await apiService.makeRequest(
            `/api/absences/conflicts/${formData.employee_id}?start_date=${formData.start_date}&end_date=${formData.end_date}`
          );
          setConflicts(conflictResult.conflicts || []);
        } catch (conflictError) {
          // Conflicts check is optional, don't show error for this
          console.warn('Failed to check conflicts:', conflictError);
          setConflicts([]);
        }
      }
      
    } catch (error) {
      console.error('Validation failed:', error);
      // Only show error toast if it's not a missing field issue
      const errorMessage = error.message || error.response?.data?.message || 'Failed to validate absence request';
      if (!errorMessage.includes('employee') && !errorMessage.includes('required')) {
        toast({
          title: 'Validation Error',
          description: errorMessage,
          variant: 'destructive'
        });
      }
      // Set validation to null on error so form can still be submitted if user wants to override
      setValidation(null);
    } finally {
      setIsValidating(false);
    }
  };

  const applyQuickAction = (action) => {
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setFormData(prev => {
      const newData = {
        ...prev,
        ...action.default_data,
        start_date: action.id === 'sick_day' ? today : tomorrow,
        end_date: action.id === 'sick_day' ? today : tomorrow,
        template_id: action.template_id
      };
      
      // Ensure employee_id is preserved if it was already set
      if (prev.employee_id && !newData.employee_id) {
        newData.employee_id = prev.employee_id;
      }
      
      return newData;
    });
    
    // Show a helpful message if employee is not selected
    if (!formData.employee_id) {
      setTimeout(() => {
        toast({
          title: 'Quick Action Applied',
          description: 'Please select an employee to complete the request',
          duration: 3000
        });
      }, 100);
    }
  };

  const applyTemplate = (template) => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setFormData(prev => ({
      ...prev,
      leave_type: template.leave_type,
      total_hours: template.default_duration_hours,
      purpose: template.description,
      requires_documentation: template.requires_documentation,
      start_date: tomorrow,
      end_date: tomorrow,
      template_id: template.id
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate employee_id is set
    if (!formData.employee_id) {
      toast({
        title: 'Employee Required',
        description: 'Please select an employee for this absence request',
        variant: 'destructive'
      });
      return;
    }
    
    if (!validation?.is_valid && !formData.policy_override_requested) {
      const hasErrors = validation?.violations?.some(v => v.severity === 'error');
      if (hasErrors) {
        toast({
          title: 'Validation Failed',
          description: 'Please resolve all errors or request a policy override',
          variant: 'destructive'
        });
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Submit failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getViolationIcon = (severity) => {
    switch (severity) {
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getViolationColor = (severity) => {
    switch (severity) {
      case 'error': return 'destructive';
      case 'warning': return 'warning';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Zap className="w-5 h-5 mr-2" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {quickActions.slice(0, 4).map((action) => (
                <Button
                  key={action.id}
                  variant="outline"
                  size="sm"
                  onClick={() => applyQuickAction(action)}
                  className="h-auto p-3 flex flex-col items-center space-y-1"
                >
                  <span className="text-xs font-medium">{action.name}</span>
                  <span className="text-xs text-gray-500">{action.description}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      <Card>
        <CardHeader>
          <CardTitle>
            {initialData ? 'Edit Absence Request' : 'New Absence Request'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Selection - Role-based (Requirements 7.1, 7.2) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee_id">Employee *</Label>
                {isHROrAdmin ? (
                  /* HR/Admin users: show employee selector with all active employees (Requirement 7.1) */
                  <EmployeeCombobox
                    employees={employees}
                    value={formData.employee_id || ""}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                    placeholder="Select an employee"
                    emptyMessage="No employees available"
                  />
                ) : (
                  /* Regular employees: pre-select and lock employee field to their own record (Requirement 7.2) */
                  <div className="flex items-center">
                    <Input
                      value={formData.employee_id && employees.find(e => e.id === formData.employee_id)
                        ? `${employees.find(e => e.id === formData.employee_id).first_name} ${employees.find(e => e.id === formData.employee_id).last_name}`
                        : currentUser?.name || 'Current User'}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                    />
                    <input type="hidden" name="employee_id" value={formData.employee_id || ''} />
                  </div>
                )}
                {!isHROrAdmin && (
                  <p className="text-xs text-gray-500 mt-1">
                    You can only submit absence requests for yourself
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="leave_type">Leave Type *</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, leave_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PTO">PTO</SelectItem>
                    <SelectItem value="SICK">Sick Leave</SelectItem>
                    <SelectItem value="PERSONAL">Personal</SelectItem>
                    <SelectItem value="MEDICAL">Medical</SelectItem>
                    <SelectItem value="BEREAVEMENT">Bereavement</SelectItem>
                    <SelectItem value="JURY_DUTY">Jury Duty</SelectItem>
                    <SelectItem value="EMERGENCY">Emergency</SelectItem>
                    <SelectItem value="UNPAID">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Partial Day Times */}
            {formData.start_date === formData.end_date && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_time">Start Time (optional)</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="end_time">End Time (optional)</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Total Time Display */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Time:</span>
                <span className="text-lg font-bold">
                  {formData.total_hours}h {formData.total_minutes > 0 && `${formData.total_minutes}m`}
                </span>
              </div>
            </div>

            {/* Purpose */}
            <div>
              <Label htmlFor="purpose">Purpose/Reason *</Label>
              <Input
                value={formData.purpose}
                onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                placeholder="Brief description of the reason for absence"
                required
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional information..."
                rows={3}
              />
            </div>

            {/* Coverage Information */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="coverage_arranged"
                  checked={formData.coverage_arranged}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, coverage_arranged: checked }))}
                />
                <Label htmlFor="coverage_arranged" className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Coverage has been arranged
                </Label>
              </div>

              {formData.coverage_arranged && (
                <div>
                  <Label>Coverage Employees</Label>
                  <EmployeeCombobox
                    employees={employees.filter(emp => emp.id !== formData.employee_id && !formData.coverage_employee_ids.includes(emp.id))}
                    value=""
                    onValueChange={(value) => {
                      if (value && !formData.coverage_employee_ids.includes(value)) {
                        setFormData(prev => ({
                          ...prev,
                          coverage_employee_ids: [...prev.coverage_employee_ids, value]
                        }));
                      }
                    }}
                    placeholder="Add coverage employee"
                    emptyMessage="No employees available"
                  />
                  
                  {formData.coverage_employee_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.coverage_employee_ids.map((empId) => {
                        const emp = employees.find(e => e.id === empId);
                        return emp ? (
                          <Badge key={empId} variant="secondary" className="flex items-center">
                            {emp.first_name} {emp.last_name}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                coverage_employee_ids: prev.coverage_employee_ids.filter(id => id !== empId)
                              }))}
                              className="ml-1 text-xs"
                            >
                              ×
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Advanced Options Toggle */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </Button>

            {showAdvanced && (
              <div className="space-y-4 border-t pt-4">
                <div>
                  <Label htmlFor="emergency_contact">Emergency Contact</Label>
                  <Input
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact: e.target.value }))}
                    placeholder="Emergency contact information"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_documentation"
                    checked={formData.requires_documentation}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_documentation: checked }))}
                  />
                  <Label htmlFor="requires_documentation" className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    Documentation required
                  </Label>
                </div>

                {formData.requires_documentation && (
                  <div>
                    <Label htmlFor="documentation_url">Documentation URL</Label>
                    <Input
                      value={formData.documentation_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, documentation_url: e.target.value }))}
                      placeholder="Link to supporting documentation"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Validation Results */}
            {validation && (
              <div className="space-y-3">
                <Separator />
                <h4 className="font-medium">Validation Results</h4>
                
                {validation.violations?.length > 0 && (
                  <div className="space-y-2">
                    {validation.violations.map((violation, index) => (
                      <Alert key={index} variant={getViolationColor(violation.severity)}>
                        <div className="flex items-start space-x-2">
                          {getViolationIcon(violation.severity)}
                          <div className="flex-1">
                            <AlertDescription>
                              <strong>{violation.type.replace('_', ' ').toUpperCase()}:</strong> {violation.message}
                              {violation.can_override && (
                                <div className="mt-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFormData(prev => ({ ...prev, policy_override_requested: true }))}
                                  >
                                    Request Override
                                  </Button>
                                </div>
                              )}
                            </AlertDescription>
                          </div>
                        </div>
                      </Alert>
                    ))}
                  </div>
                )}

                {conflicts.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-orange-600">Scheduling Conflicts</h5>
                    {conflicts.map((conflict, index) => (
                      <Alert key={index} variant="warning">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription>
                          <strong>{conflict.conflict_type.replace('_', ' ').toUpperCase()}:</strong> {conflict.message}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}

                {validation.auto_approve_eligible && (
                  <Alert>
                    <CheckCircle className="w-4 h-4" />
                    <AlertDescription>
                      This request is eligible for automatic approval.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Policy Override */}
            {formData.policy_override_requested && (
              <div className="space-y-3 border-t pt-4">
                <h4 className="font-medium text-orange-600">Policy Override Request</h4>
                <div>
                  <Label htmlFor="policy_override_reason">Override Reason *</Label>
                  <Textarea
                    value={formData.policy_override_reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, policy_override_reason: e.target.value }))}
                    placeholder="Please provide a detailed reason for requesting a policy override..."
                    required
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || isValidating}
                className="min-w-[120px]"
              >
                {isSubmitting ? 'Submitting...' : initialData ? 'Update Request' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedAbsenceForm;